import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { fg } from '@confluence/feature-gating';

import type { CreateIssueResponse } from '../__types__/apiUtils';
import { messages } from '../messages';
import {
	useBulkCreateContext,
	useResetSingleIssuesCreated,
} from '../providers/BulkCreateContextProvider';
import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload';
import type { InsertMultipleStorageFragmentsRequestPayload } from '../__types__/InsertMultipleStorageFragmentsRequestPayload';
import type {
	CellModification,
	InsertStorageColumnTableRequestPayload,
} from '../__types__/InsertStorageColumnTableRequestPayload';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents';

import { useBulkCreateFlags } from './useBulkCreateFlags';
import {
	bulkConstructSmartlinkTags,
	constructSmartlinkTag,
	constructHeading,
	constructJiraIssueUrl,
	insertContentAtSelectionEnd,
	updateLastFetchTimeIfNotViewMode,
	constructBreakContainerTag,
	insertMultipleContentAtSelectionEnd,
	insertWorkItemsIntoTable,
} from './issueInsertionUtils';
import { expVal } from '@confluence/feature-experiments';

const JIRA_SINGLE_ISSUE_LINK_INSERTION = 'jiraSingleIssueLinkInsertion';
const JIRA_MULTIPLE_ISSUE_LINK_INSERTION = 'jiraMultipleIssueLinkInsertion';

export const useGetIssueInsertionActions = () => {
	const { formatMessage } = useIntl();
	const [
		{
			currentJiraSite,
			insertStorageFragmentRequestPayload,
			singleIssuesCreated,
			isCreatedWithAi,
			isCreatedFromActionItems,
			isCreatedFromTable,
		},
	] = useBulkCreateContext();
	const [
		{
			insertMultipleStorageFragmentsRequestPayload,
			insertStorageColumnTableRequestPayload,
			pageMode,
			hasEditPermission,
			selectedTable,
			editorAPI,
		},
	] = useIssueCreateSidePanelContext();
	const {
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
		triggerMultipleIssuesCreatedFlag,
	} = useBulkCreateFlags();
	const resetSingleIssuesCreated = useResetSingleIssuesCreated();
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const headingString = formatMessage(
		fg('confluence-issue-terminology-refresh')
			? messages.bulkCreateIssueInsertionHeadingIssueTermRefresh
			: messages.bulkCreateIssueInsertionHeading,
	);
	const currentUrl = useRouteDataRef().current?.href;

	const constructXmlModification = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined,
		) => {
			const smartlinkTags = bulkConstructSmartlinkTags(
				createdIssues.map((issue) => constructJiraIssueUrl(currentJiraSite?.url, issue.key)),
			);
			return insertStorageFragmentRequestPayload?.selectedText !== headingString
				? `${constructHeading(headingString)}<br/>${smartlinkTags}`
				: `<p>${smartlinkTags}</p>`;
		},
		[currentJiraSite?.url, headingString],
	);

	const constructXmlModifications = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			fragments: InsertMultipleStorageFragmentsRequestPayload['fragments'],
		) => {
			const createdIssueRowIds = new Set(createdIssues.map((issue) => String(issue.rowIndex)));
			return fragments
				.filter((fragment) => createdIssueRowIds.has(fragment.rowIndex))
				.map((fragment, index) => ({
					...fragment,
					xmlModification: constructBreakContainerTag(
						constructSmartlinkTag(
							constructJiraIssueUrl(currentJiraSite?.url, createdIssues[index].key),
						),
					),
				}));
		},
		[currentJiraSite?.url],
	);

	const constructCellModifications = useCallback(
		(createdIssues: CreateIssueResponse[]): CellModification[] | undefined => {
			return createdIssues
				.filter((issue) => issue.rowIndex !== undefined)
				.map((issue) => {
					const xmlInsertion = constructSmartlinkTag(
						constructJiraIssueUrl(currentJiraSite?.url, issue.key),
					);
					return { rowIndex: Number(issue.rowIndex), xmlInsertion };
				});
		},
		[currentJiraSite?.url],
	);

	const constructAnchorLinkToHeading = useCallback(() => {
		const formattedHeading = headingString.split(' ').join('-');
		const [baseUrl, existingHash] = (currentUrl ?? '').split('#');
		return existingHash ? `${baseUrl}#${formattedHeading}` : `${currentUrl}#${formattedHeading}`;
	}, [currentUrl, headingString]);

	const reloadOnClick = useCallback(() => {
		// eslint-disable-next-line no-restricted-syntax
		window.location.href = constructAnchorLinkToHeading();
		window.location.reload();
	}, [constructAnchorLinkToHeading]);

	const handleStorageInsertion = useCallback(
		(
			issuesCreated: CreateIssueResponse[],
			payload:
				| InsertStorageFragmentRequestPayload
				| InsertStorageColumnTableRequestPayload
				| InsertMultipleStorageFragmentsRequestPayload,
		) => {
			let showReloadAction = false;
			updateLastFetchTimeIfNotViewMode(payload, pageMode);
			let insertPromise = Promise.resolve(false);
			if (isCreatedWithAi) {
				insertPromise = insertContentAtSelectionEnd(payload as InsertStorageFragmentRequestPayload);
			} else if (isCreatedFromActionItems) {
				insertPromise = insertMultipleContentAtSelectionEnd(
					payload as InsertMultipleStorageFragmentsRequestPayload,
				);
			} else if (isCreatedFromTable) {
				insertPromise = insertWorkItemsIntoTable(
					issuesCreated,
					currentJiraSite?.url,
					pageMode,
					selectedTable,
					editorAPI,
					payload as InsertStorageColumnTableRequestPayload,
				);
			}
			insertPromise
				.then((data) => {
					// There is a case where the API response is OK, but the response data is false.
					// When this happens, the backend has trouble inserting the ticket to the page (likely due to a payload error) and we should show an error toast.
					if (data === false) {
						throw new Error('Failed to insert storage');
					}
				})
				.then(() => {
					showReloadAction = expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
						? pageMode === 'view'
						: true;
					void resetSingleIssuesCreated();
				})
				.catch((_error) => {
					if (issuesCreated.length === 1) {
						triggerSingleIssueInsertLinkFailureFlag({
							issueKey: issuesCreated[0].key,
							summary: issuesCreated[0].summary,
						});
						logRequestFailed({
							requestName: JIRA_SINGLE_ISSUE_LINK_INSERTION,
							errorMessage: (_error as Error).message,
						});
					} else {
						triggerMultipleIssuesInsertLinkFailureFlag(issuesCreated);
						logRequestFailed({
							requestName: JIRA_MULTIPLE_ISSUE_LINK_INSERTION,
							errorMessage: (_error as Error).message,
						});
					}
				})
				.finally(() => {
					triggerMultipleIssuesCreatedFlag(issuesCreated, reloadOnClick, showReloadAction);
				});
		},
		[
			isCreatedFromActionItems,
			isCreatedFromTable,
			isCreatedWithAi,
			logRequestFailed,
			pageMode,
			reloadOnClick,
			resetSingleIssuesCreated,
			triggerMultipleIssuesCreatedFlag,
			triggerMultipleIssuesInsertLinkFailureFlag,
			triggerSingleIssueInsertLinkFailureFlag,
			selectedTable,
			currentJiraSite?.url,
			editorAPI,
		],
	);

	const addSingleIssuesToPage = useCallback(() => {
		//If the user doesn't have edit permissions on the page, we shouldn't attempt to insert the issues.
		if (singleIssuesCreated.length > 0 && !hasEditPermission) {
			triggerMultipleIssuesCreatedFlag(singleIssuesCreated, reloadOnClick, false);
			void resetSingleIssuesCreated();
		} else if (
			insertStorageFragmentRequestPayload &&
			isCreatedWithAi &&
			singleIssuesCreated.length > 0
		) {
			insertStorageFragmentRequestPayload.xmlModification = constructXmlModification(
				singleIssuesCreated,
				insertStorageFragmentRequestPayload,
			);
			handleStorageInsertion(singleIssuesCreated, insertStorageFragmentRequestPayload);
		} else if (
			insertStorageColumnTableRequestPayload &&
			!isCreatedWithAi &&
			singleIssuesCreated.length > 0
		) {
			insertStorageColumnTableRequestPayload.cellModifications =
				constructCellModifications(singleIssuesCreated);
			handleStorageInsertion(singleIssuesCreated, insertStorageColumnTableRequestPayload);
		} else if (
			!isCreatedWithAi &&
			!isCreatedFromTable &&
			isCreatedFromActionItems &&
			singleIssuesCreated.length > 0 &&
			insertMultipleStorageFragmentsRequestPayload &&
			fg('confluence-create-work-items-from-action-items')
		) {
			handleStorageInsertion(singleIssuesCreated, {
				pageId: insertMultipleStorageFragmentsRequestPayload.pageId,
				lastFetchTime: insertMultipleStorageFragmentsRequestPayload.lastFetchTime,
				fragments: constructXmlModifications(
					singleIssuesCreated,
					insertMultipleStorageFragmentsRequestPayload.fragments,
				),
			});
		}
	}, [
		singleIssuesCreated,
		hasEditPermission,
		insertStorageFragmentRequestPayload,
		isCreatedWithAi,
		insertStorageColumnTableRequestPayload,
		isCreatedFromTable,
		isCreatedFromActionItems,
		insertMultipleStorageFragmentsRequestPayload,
		triggerMultipleIssuesCreatedFlag,
		reloadOnClick,
		resetSingleIssuesCreated,
		constructXmlModification,
		handleStorageInsertion,
		constructCellModifications,
		constructXmlModifications,
	]);

	return {
		constructAnchorLinkToHeading,
		constructXmlModification,
		constructXmlModifications,
		constructCellModifications,
		addSingleIssuesToPage,
	};
};
